import React, { useState, useEffect } from 'react';
import { db } from '../JavaScript/firebaseConfig';
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  TextField,
  Rating,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  Collapse,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/system';
import '../Css/ReviewsSection_css.css';

// ---------- Styled (compact + modern) ----------
const Wrap = styled(Box)({
  backgroundColor: '#f9f9fb',
  borderRadius: 16,
  padding: '24px',
  boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
});

const HeaderBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
  marginBottom: 12,
});

const Summary = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
});

const WriteBtn = styled(Button)({
  backgroundColor: '#593EA1',
  color: '#fff',
  textTransform: 'none',
  borderRadius: 10,
  padding: '8px 14px',
  '&:hover': { backgroundColor: '#452F82' },
});

const FormWrap = styled(Box)({
  background: '#ffffff',
  borderRadius: 12,
  padding: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  marginTop: 8,
});

const ReviewCard = styled(Card)({
  borderRadius: 12,
  boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
  backgroundColor: '#ffffff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const CardTop = styled(CardHeader)({
  paddingBottom: 0,
});

const CardBody = styled(CardContent)({
  paddingTop: 8,
});

const LoadMoreButton = styled(Button)({
  margin: '12px auto 0',
  display: 'block',
  backgroundColor: '#593EA1',
  color: '#ffffff',
  textTransform: 'none',
  borderRadius: 10,
  '&:hover': { backgroundColor: '#452F82' },
});

// ---------- Helpers ----------
function formatRelative(ts) {
  if (!ts?.seconds) return 'Just now';
  const date = new Date(ts.seconds * 1000);
  const diffMs = Date.now() - date.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min${min > 1 ? 's' : ''} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? 's' : ''} ago`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

const clamp = (text, max = 300) => (text?.length > max ? text.slice(0, max) + '…' : text);

// ---------- Component ----------
const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastReviewDoc, setLastReviewDoc] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const reviewsCollection = collection(db, 'reviews');

  useEffect(() => {
    fetchInitialReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInitialReviews = async () => {
    try {
      setLoadingInit(true);
      const queryRef = query(reviewsCollection, orderBy('timestamp', 'desc'), limit(4));
      const data = await getDocs(queryRef);
      const fetched = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setReviews(fetched);
      setLastReviewDoc(data.docs[data.docs.length - 1] || null);
    } catch (err) {
      console.error('Error fetching initial reviews:', err);
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchMoreReviews = async () => {
    try {
      if (!lastReviewDoc) return;
      setLoadingMore(true);
      const queryRef = query(
        reviewsCollection,
        orderBy('timestamp', 'desc'),
        startAfter(lastReviewDoc),
        limit(4)
      );
      const data = await getDocs(queryRef);
      const fetched = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setReviews((prev) => [...prev, ...fetched]);
      setLastReviewDoc(data.docs[data.docs.length - 1] || null);
    } catch (err) {
      console.error('Error fetching more reviews:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (name.trim().length < 2 || name.trim().length > 20) {
      setError('Name must be between 2 and 20 characters.');
      return;
    }
    if (comment.trim().length < 10 || comment.trim().length > 200) {
      setError('Comment must be between 10 and 200 characters.');
      return;
    }
    if (!rating || rating < 1) {
      setError('Please select a rating.');
      return;
    }

    try {
      await addDoc(reviewsCollection, {
        name: name.trim(),
        comment: comment.trim(),
        rating,
        timestamp: serverTimestamp(),
      });
      setName('');
      setComment('');
      setRating(0);
      setOpenForm(false);
      setSuccess('Thanks! Your review was submitted.');
      // refresh to show newest first
      fetchInitialReviews();
    } catch (err) {
      setError('Error adding review: ' + err.message);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Compute simple summary from loaded reviews (no backend changes)
  const loadedCount = reviews.length;
  const avg =
    loadedCount > 0
      ? (reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / loadedCount).toFixed(1)
      : '—';

  return (
    <Wrap className="review-section">
      <HeaderBar>
        <Box>
          <Typography variant="h5" className="review-title" sx={{ mb: 0.5 }}>
            Customer Reviews
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            We value your feedback — it helps us improve every order.
          </Typography>
        </Box>

        <Summary>
          <Rating value={Number(isNaN(avg) ? 0 : avg)} precision={0.1} readOnly size="small" />
          <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
            {avg}/5
          </Typography>
          <Typography variant="body2" sx={{ color: '#777' }}>({loadedCount} shown)</Typography>
          <WriteBtn onClick={() => setOpenForm((o) => !o)}>
            {openForm ? 'Close' : 'Write a review'}
          </WriteBtn>
        </Summary>
      </HeaderBar>

      <Collapse in={openForm} timeout={250} unmountOnExit>
        <FormWrap component="form" onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
                fullWidth
                size="small"
                multiline
                rows={3}
                inputProps={{ maxLength: 200 }}
                helperText={`${comment.length}/200`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Rating</Typography>
              <Rating
                name="rating"
                value={rating}
                onChange={(_e, newValue) => setRating(newValue)}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <Button type="submit" variant="contained" sx={{ backgroundColor: '#593EA1', '&:hover': { backgroundColor: '#452F82' } }}>
                Submit
              </Button>
            </Grid>
          </Grid>
        </FormWrap>
      </Collapse>

      {/* Reviews grid */}
      <Box className="reviews-list" sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {reviews.map((review) => {
            const isExpanded = !!expandedIds[review.id];
            const displayComment = isExpanded ? review.comment : clamp(review.comment, 140);
            const initials = (review.name || '?').trim().charAt(0).toUpperCase();
            return (
              <Grid item xs={12} md={6} key={review.id}>
                <ReviewCard>
                  <CardTop
                    avatar={<Avatar sx={{ bgcolor: '#593EA1' }}>{initials}</Avatar>}
                    title={<Typography sx={{ fontWeight: 700 }}>{review.name}</Typography>}
                    subheader={formatRelative(review.timestamp)}
                  />
                  <CardBody>
                    <Rating value={Number(review.rating) || 0} readOnly size="small" />
                    <Typography variant="body2" className={`clamped-text ${isExpanded ? 'expanded' : ''}`} sx={{ mt: 1 }}>
                      {displayComment}
                    </Typography>
                    {review.comment && review.comment.length > 140 && (
                      <Button
                        onClick={() => toggleExpand(review.id)}
                        size="small"
                        sx={{ mt: 0.5, textTransform: 'none' }}
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                      </Button>
                    )}
                  </CardBody>
                </ReviewCard>
              </Grid>
            );
          })}
        </Grid>

        {/* Load more */}
        {lastReviewDoc && (
          <LoadMoreButton onClick={fetchMoreReviews} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more reviews'}
          </LoadMoreButton>
        )}

        {/* Empty state */}
        {!loadingInit && reviews.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: '#777', mt: 2 }}>
            No reviews yet. Be the first to share your experience!
          </Typography>
        )}
      </Box>

      <Snackbar
        open={!!success}
        autoHideDuration={2500}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Wrap>
  );
};

export default ReviewsSection;
